import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

/**
 * Centraliza checagens de permissão e rota ativa.
 * Todas as funções são estáveis (useCallback/useMemo) pra permitir uso em
 * deps de outros hooks sem re-renders desnecessários.
 *
 * @returns {{
 *   permissions: string[],
 *   hasPermission: (key: string) => boolean,
 *   hasAnyPermission: (keys?: string[]) => boolean,
 *   isActive: (path: string) => boolean,
 *   pathname: string
 * }}
 */
export default function usePermissions() {
   const userPermissions = useSelector((state) => state.auth.user?.permissions);
   const permissions = useMemo(
      () => userPermissions || [],
      [userPermissions]
   );
   const { pathname } = useLocation();

   const permissionsSet = useMemo(() => new Set(permissions), [permissions]);

   const hasPermission = useCallback(
      (key) => permissionsSet.has(key),
      [permissionsSet]
   );

   // Array vazio ou undefined = sem restrição (retorna true)
   const hasAnyPermission = useCallback(
      (keys = []) => !keys.length || keys.some((k) => permissionsSet.has(k)),
      [permissionsSet]
   );

   const isActive = useCallback((path) => pathname === path, [pathname]);

   return {
      permissions,
      hasPermission,
      hasAnyPermission,
      isActive,
      pathname,
   };
}
